/* ============================================================================
   ACEPLORE · module-wishlist.js — localStorage wishlist + drawer (<lumina-wishlist>).
   Captures product metadata from cards/PDP so the drawer renders with no fetch.
   ========================================================================== */
(function () {
  'use strict';
  const { utils, events, EVENTS } = window.Lumina;
  const KEY = 'lumina:wishlist';

  const Store = {
    all() { return utils.storage.get(KEY, []); },
    has(handle) { return this.all().some((i) => i.handle === handle); },
    add(item) {
      const list = this.all();
      if (!list.some((i) => i.handle === item.handle)) { list.unshift(item); utils.storage.set(KEY, list); }
      emit();
    },
    remove(handle) { utils.storage.set(KEY, this.all().filter((i) => i.handle !== handle)); emit(); },
    toggle(item) { this.has(item.handle) ? this.remove(item.handle) : this.add(item); return this.has(item.handle); },
    count() { return this.all().length; },
  };
  window.Lumina.wishlist = Store;
  function emit() { events.emit(EVENTS.WISHLIST_UPDATE, Store.all()); }

  // Capture metadata for a product from the nearest card or PDP.
  function metaFromButton(btn) {
    const card = btn.closest('[data-product-card], [data-product-meta]');
    const handle = btn.dataset.productHandle || (card && card.dataset.productHandle);
    if (!handle) return null;
    let title = '', image = '', price = '', url = '/products/' + handle, variantId = '';
    if (card) {
      // Selector unions cover both contexts: product cards AND the PDP
      // ([data-product-meta]), which uses .lm-product__title and keeps its
      // variant id in the product form's hidden input.
      title = card.querySelector('.lm-card__link, [data-product-title], .lm-product__title')?.textContent.trim() || '';
      image = card.querySelector('.lm-card__img--primary, [data-product-image]')?.currentSrc || card.querySelector('img')?.src || '';
      price = card.querySelector('.lm-price__amount, [data-product-price]')?.textContent.trim() || '';
      url = card.querySelector('a[href*="/products/"]')?.getAttribute('href') || url;
      variantId = card.querySelector('[data-variant-id]')?.dataset.variantId
        || card.querySelector('form[data-product-form] input[name="id"], input[name="id"]')?.value || '';
    }
    return { handle, title, image, price, url, variantId };
  }

  // Toggle buttons (on cards + PDP)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    const meta = metaFromButton(btn);
    if (!meta) return;
    const nowSaved = Store.toggle(meta);
    Lumina.toast(nowSaved ? utils.t('wishlistAdded', 'Saved to wishlist') : utils.t('wishlistRemoved', 'Removed'), { type: 'success' });
  });

  // Reflect saved state on all toggle buttons
  function syncButtons() {
    utils.qsa('[data-wishlist-toggle]').forEach((btn) => {
      const handle = btn.dataset.productHandle || btn.closest('[data-product-card]')?.dataset.productHandle;
      const saved = handle && Store.has(handle);
      btn.setAttribute('aria-pressed', String(!!saved));
      const icon = btn.querySelector('.lm-icon');
      if (icon) icon.style.fill = saved ? 'currentColor' : 'none';
    });
  }
  function syncCount(list) {
    const n = (list || Store.all()).length;
    utils.qsa('[data-wishlist-count]').forEach((b) => { b.textContent = n; b.hidden = n === 0; });
  }
  events.on(EVENTS.WISHLIST_UPDATE, (list) => { syncButtons(); syncCount(list); });
  events.on('section:load', syncButtons);

  /* ---- Drawer ----------------------------------------------------------- */
  class LuminaWishlist extends window.Lumina.Element {
    connectedCallback() {
      this.overlay = this.querySelector('[data-wishlist-overlay]');
      this.panel = this.querySelector('.lm-drawer');
      this.itemsEl = this.querySelector('[data-wishlist-items]');
      this.emptyEl = this.querySelector('[data-wishlist-empty]');
      this.tpl = this.querySelector('[data-wishlist-item-template]');
      this.release = null;
      this.listen(document, 'click', (e) => { if (e.target.closest('[data-wishlist-open]')) { e.preventDefault(); this.open(); } });
      this.querySelectorAll('[data-wishlist-close]').forEach((b) => this.listen(b, 'click', () => this.close()));
      this.listen(this.overlay, 'click', () => this.close());
      this.listen(document, 'keydown', (e) => { if (e.key === 'Escape' && this.isOpen) this.close(); });
      this.sub(EVENTS.WISHLIST_UPDATE, () => { if (this.isOpen) this.render(); });
      this.sub(EVENTS.CART_ADD, () => { if (this.isOpen) this.close(); });
      this.listen(this.itemsEl, 'click', (e) => {
        const rm = e.target.closest('[data-wl-remove]');
        if (rm) {
          const item = rm.closest('[data-wishlist-item]');
          this._restoreFocusIndex = utils.qsa('[data-wishlist-item]', this.itemsEl).indexOf(item);
          Store.remove(item.dataset.handle);
        }
      });
    }
    open() { if (this.isOpen) return; this.isOpen = true; this.render(); utils.lockScroll(true); this.overlay.setAttribute('aria-hidden', 'false'); this.panel.removeAttribute('inert'); this.panel.setAttribute('aria-hidden', 'false'); utils.qsa('[data-wishlist-open]').forEach((el) => el.setAttribute('aria-expanded', 'true')); this.release = utils.trapFocus(this.panel, document.activeElement); }
    close() { if (!this.isOpen) return; this.isOpen = false; utils.lockScroll(false); this.release && this.release(); this.release = null; this.overlay.setAttribute('aria-hidden', 'true'); this.panel.setAttribute('aria-hidden', 'true'); this.panel.setAttribute('inert', ''); utils.qsa('[data-wishlist-open]').forEach((el) => el.setAttribute('aria-expanded', 'false')); }
    render() {
      const list = Store.all();
      const restoreIndex = this._restoreFocusIndex;
      this._restoreFocusIndex = null;
      this.querySelector('[data-wishlist-count-text]').textContent = list.length ? `(${list.length})` : '';
      if (!list.length) {
        this.emptyEl.hidden = false; this.itemsEl.hidden = true; this.itemsEl.innerHTML = '';
        if (restoreIndex != null) requestAnimationFrame(() => this.emptyEl.querySelector('button, a')?.focus({ preventScroll: true }));
        return;
      }
      this.emptyEl.hidden = true; this.itemsEl.hidden = false;
      this.itemsEl.innerHTML = '';
      list.forEach((item) => {
        const node = this.tpl.content.cloneNode(true);
        const li = node.querySelector('[data-wishlist-item]');
        li.dataset.handle = item.handle;
        node.querySelectorAll('[data-wl-url]').forEach((a) => (a.href = item.url));
        const img = node.querySelector('[data-wl-image]'); if (item.image) img.src = item.image; else img.remove();
        node.querySelector('[data-wl-title]').textContent = item.title;
        node.querySelector('[data-wl-price]').textContent = item.price;
        const add = node.querySelector('[data-wl-add]');
        if (item.variantId) add.dataset.variantId = item.variantId; else add.remove();
        this.itemsEl.appendChild(node);
      });
      if (restoreIndex != null) requestAnimationFrame(() => {
        const rows = utils.qsa('[data-wishlist-item]', this.itemsEl);
        const row = rows[Math.min(restoreIndex, rows.length - 1)];
        (row?.querySelector('[data-wl-remove]') || row?.querySelector('[data-wl-title]'))?.focus({ preventScroll: true });
      });
    }
  }
  if (!customElements.get('lumina-wishlist')) customElements.define('lumina-wishlist', LuminaWishlist);

  // Init
  syncButtons(); syncCount();
})();
